// Q4 targets CSV: stage the strategists' research file for the loader.
//
// The file is read as text in the browser and posted as JSON; the server stores it and answers
// with its row count, size and hash. Storing is all the upload does: the load is
// `jobs/load_q4_targets.py --from-upload`, run on the service. The page never shows the file's
// contents, only the receipt and what is staged.
import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchQ4Upload, panelMessage, uploadQ4Targets } from '../api/client';
import type { Q4UploadReceipt } from '../api/types';
import { useAuthContext } from '../auth/AuthContext';
import { isCsvFileName, q4UploadSummary } from './q4Upload';

const STAGED_QUERY_KEY = ['admin-q4-upload'];

export function Q4UploadBox() {
  const { getToken } = useAuthContext();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState<{ reply: Q4UploadReceipt; fileName: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // What is staged now, read once on mount and again after an upload.
  const stagedQuery = useQuery({ queryKey: STAGED_QUERY_KEY, queryFn: () => fetchQ4Upload(getToken) });
  const staged = stagedQuery.data?.upload ?? null;

  async function upload(event: ChangeEvent<HTMLInputElement>) {
    const input = event.target;
    const file = input.files?.[0];
    if (!file || busy) return;
    setError(null);
    setReceipt(null);

    // The accept attribute is a hint; the name is checked here and again on the server.
    if (!isCsvFileName(file.name)) {
      setError('Pick a .csv file.');
      input.value = '';
      return;
    }

    // Read in the browser, send as text, then refresh the staged line.
    setBusy(true);
    try {
      const content = await file.text();
      const reply = await uploadQ4Targets(getToken, { file_name: file.name, content });
      setReceipt({ reply, fileName: file.name });
      await queryClient.invalidateQueries({ queryKey: STAGED_QUERY_KEY });
    } catch (caught) {
      setError(panelMessage(caught, 'The upload could not be stored.'));
    } finally {
      setBusy(false);
      input.value = '';
    }
  }

  return (
    <>
      <div className="grantform">
        <label className="field">
          <span className="fieldlab">CSV file</span>
          <input type="file" accept=".csv,text/csv" disabled={busy} onChange={event => { void upload(event); }} />
        </label>
        {busy ? <span className="adminloading">Uploading…</span> : null}
      </div>

      {receipt ? (
        <p className="actionnote ok">Stored {q4UploadSummary(receipt.reply, receipt.fileName)}</p>
      ) : null}
      {error ? <p className="actionnote bad">{error}</p> : null}

      {/* What the loader would read next. */}
      {stagedQuery.isPending ? <p className="adminloading">Reading Postgres…</p> : null}
      {stagedQuery.error ? <p className="actionnote bad">{panelMessage(stagedQuery.error, 'Could not read the staged upload.')}</p> : null}
      {stagedQuery.data ? (
        <p className="adminnote">{staged ? `Staged: ${q4UploadSummary(staged)}` : 'Nothing staged yet.'}</p>
      ) : null}
      <p className="adminnote">Then run <code>jobs/load_q4_targets.py --from-upload</code> on the service.</p>
    </>
  );
}
